import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { customFetcher } from './customFetcher';

export const emailChangeStatusSchema = z.object({
    pending: z.boolean(),
    current_email: z.string().optional(),
    new_email: z.string().optional(),
    expires_at: z.string().optional(),
});

export type IEmailChangeStatus = z.infer<typeof emailChangeStatusSchema>;

const STATUS_KEY = ['email-change', 'status'];

/**
 * customFetcher wirft bei Fehlern `Error: <status> <text> - <body>`. Diese
 * Helper zieht das `detail`-Feld aus dem Body für verständliche Meldungen.
 */
export const extractApiDetail = (error: unknown): string | null => {
    if (!(error instanceof Error)) {
        return null;
    }
    const braceIndex = error.message.indexOf('{');
    if (braceIndex === -1) {
        return null;
    }
    try {
        const parsed = JSON.parse(error.message.slice(braceIndex)) as { detail?: unknown };
        return typeof parsed.detail === 'string' ? parsed.detail : null;
    } catch {
        return null;
    }
};

export const useEmailChangeStatus = (enabled = true) => {
    const { data, isLoading } = useQuery<IEmailChangeStatus>({
        queryKey: STATUS_KEY,
        enabled,
        queryFn: async () =>
            emailChangeStatusSchema.parse(await customFetcher<unknown>({ endpoint: '/accounts/email-change/' })),
        staleTime: 15_000,
    });
    return { status: data ?? null, isLoading };
};

export const useRequestEmailChange = () => {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending } = useMutation<IEmailChangeStatus, Error, string>({
        mutationFn: (newEmail) =>
            customFetcher<IEmailChangeStatus>({
                endpoint: '/accounts/email-change/',
                method: 'POST',
                body: { new_email: newEmail },
            }),
        onSuccess: (data) => {
            queryClient.setQueryData(STATUS_KEY, data);
        },
    });
    return { requestEmailChange: mutateAsync, isRequesting: isPending };
};

export const useCancelEmailChange = () => {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending } = useMutation<IEmailChangeStatus, Error, void>({
        mutationFn: () =>
            customFetcher<IEmailChangeStatus>({ endpoint: '/accounts/email-change/', method: 'DELETE' }),
        onSuccess: (data) => {
            queryClient.setQueryData(STATUS_KEY, data);
        },
    });
    return { cancelEmailChange: mutateAsync, isCanceling: isPending };
};

export const useConfirmEmailChange = () => {
    const { mutateAsync, isPending } = useMutation<{ detail: string; email: string }, Error, string>({
        mutationFn: (token) =>
            customFetcher<{ detail: string; email: string }>({
                endpoint: '/accounts/email-change/confirm/',
                method: 'POST',
                body: { token },
            }),
    });
    return { confirmEmailChange: mutateAsync, isConfirming: isPending };
};
